/*!
 * doTERRA Web-UI
 * http://www.doterra.com
 *
 * Copyright 2019 doTERRA Holdings, LLC
 * All Rights Reserved
 */
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useFormObserver } from '../contexts/form-observer';

const SET_INITIAL = 'SET_INITIAL';
const SET_VALUE = 'SET_VALUE';
const SET_VALUES = 'SET_VALUES';
const SET_DISABLED_VALUE = 'SET_DISABLED_VALUE';

const FormStateContext = React.createContext();
const FormDispatchContext = React.createContext();

const getSchemaProperty = (schema, name) => {
    return schema && schema.fields ? schema.fields[name] : undefined;
};

const validateField = (schema, name, value) => {
    const errors = [];
    const prop = getSchemaProperty(schema, name);
    if (!prop) {
        return errors;
    }
    return prop.validate(value);
};

const validateFields = (schema, values) => {
    const errors = [];
    if (!schema || !schema.fields) {
        return errors;
    } 
    for (let key in schema.fields) {
        let fieldErrors = validateField(schema, key, values[key]);
        if (fieldErrors.length > 0) {
            errors.push({name: key, errors: fieldErrors});
        }
    }
    return errors;
};

const hasDifferentErrors = (errors1, errors2) => {
    if (errors1.length !== errors2.length) {
        return true;
    }
    const errors = errors1.reduce((prev, cur) => {
        if (errors2.includes(cur)) {
            prev.push(cur);
        }
        return prev;
    }, []);
    return errors2.length !== errors.length;
};

const applyErrors = (state, name, errors) => {
    let index = state.errors.findIndex(element => element.name === name);
    if (index < 0 && errors.length === 0) {
        return state.errors;
    }
    if (index < 0 && errors.length > 0) {
        return [...state.errors, {name, errors}];
    }
    if (index >= 0 && errors.length === 0) {
        let _errors = [...state.errors];
        _errors.splice(index, 1);
        return _errors;
    }
    const fieldInfo = state.errors[index];
    if (!hasDifferentErrors(fieldInfo.errors, errors)) {
        return state.errors;
    }
    let _errors = [...state.errors];
    _errors[index] = {name, errors};
    return _errors;
};

const getDirtyValues = (state, values) => {
    const { initial } = state;
    return Object.keys(values).reduce((collector, key) => {
        if (!initial[key] || initial[key] !== values[key]) {
            collector.push(key);
        }
        return collector;
    }, []);
};

const checkDirtyField = (state, name, value) => {
    const { initial, dirty } = state;
    const isDirty = initial[name] !== value;
    let index = dirty.findIndex(element => element === name);
    if (isDirty && index >= 0 || !isDirty && index < 0) {
        return dirty;
    }
    return (index < 0) ? [...dirty, name] : [...dirty].splice(index, 1);
};

const applyChange = (state, schema, name, value) => {
    // set the value
    const values = {...state.values};
    values[name] = value;
    // check for dirty
    const dirty = checkDirtyField(state, name, value);
    // check for errors
    let errors = validateField(schema, name, value);
    errors = applyErrors(state, name, errors);
    // notifiy form observer, if any
    return {...state, values, dirty, errors};
};


const FormReducer = (state, action) => {
    switch (action.type) {
        case SET_INITIAL: {
            const { schema, notifyObserver, payload } = action;
            const values = {...payload};
            const errors = validateFields(schema, values);
            const _state = {...state, initial: values, initialized: true, values, dirty: [], errors, fieldDisabledValue: true};
            if (notifyObserver) {
                notifyObserver(_state.errors.length === 0, false);
            }
            return _state;
        }
        case SET_VALUE: {
            const { schema, notifyObserver, payload: { name, value }} = action;
            const _state = applyChange(state, schema, name, value);
            if (notifyObserver) {
                notifyObserver(_state.errors.length === 0, _state.dirty.length > 0);
            }
            return _state;
        }
        case SET_VALUES: {
            const { schema, notifyObserver, payload } = action;
            const errors = validateFields(schema, payload);
            const dirty = getDirtyValues(state, payload);
            const _state = {...state, values: payload, dirty, errors};
            if (notifyObserver) {
                notifyObserver(_state.errors.length === 0, _state.dirty.length > 0);
            }
            return _state;
        }
        case SET_DISABLED_VALUE: {
            const { fieldDisabledValue } = state;
            const oldFieldDisabledValue = fieldDisabledValue;
            const { notifyObserver, payload } = action;
            const _state = {...state, fieldDisabledValue: payload};
            if (notifyObserver) {
                notifyObserver(_state.fieldDisabledValue !== oldFieldDisabledValue);
            }
            return _state;
        }
        default: {
            throw new Error(`[Form.FormReducer] Unsupported action type: ${action.type}.`);
        }
    }
};

export const Form = ({id, name, schema, initial, onSubmit, children }) => {
    [schema] = useState(schema ? schema : {});
    const errors = initial ? validateFields(schema, initial) : [];
    initial = initial || {};
    const [state, dispatch] = useReducer(FormReducer, {initial, initialized: false, values: initial, dirty: [], errors, fieldDisabledValue: true});
    const { notifyObserver, registerForm } = useFormObserver(); // all these values could be undefined

    const handleSubmit = (e) => {
        if (e) {
            e.preventDefault();
        }
        if (onSubmit) {
            onSubmit(state.values);
        }
        // e.preventDefault();
    };

    if (registerForm) {
        useEffect(() => {
            registerForm(handleSubmit);
        }, [state.values]);
    }

    return (
        <FormStateContext.Provider value={state}> 
            <FormDispatchContext.Provider value={{dispatch, schema, notifyObserver}}>
                <form name={name} id={id} onSubmit={handleSubmit}>
                    {children}
                </form>
            </FormDispatchContext.Provider>
        </FormStateContext.Provider>
    );

};

export const useFormState = () => {
    const context = useContext(FormStateContext);
    if (!context) {
        throw new Error('useFormState must be used within a Form');
    }
    return {...context, isDirty: context.dirty.length > 0, isValid: context.errors.length === 0};
};


export const useFormMutator = () => {
    const { dispatch, schema, notifyObserver } = useContext(FormDispatchContext);
    if (!dispatch) {
        throw new Error('useFormMutator must be used within a Form');
    }
    const setInitialValues = (values) => {
        dispatch({type: SET_INITIAL, schema, notifyObserver, payload: values});
    };
    const setFieldValue = (name, value) => {
        dispatch({type: SET_VALUE, schema, notifyObserver, payload: {name, value}});
    };
    const setFieldValues = (values) => {
        dispatch({type: SET_VALUES, schema, notifyObserver, payload: values});
    };
    const setFieldDisabledValue = (value) => {
        dispatch({type: SET_DISABLED_VALUE, notifyObserver, payload: value});
    };
    return { setInitialValues, setFieldValue, setFieldValues, setFieldDisabledValue };
};


export const useFormField = (name) => {
    const { setFieldValue } = useFormMutator();
    const { values, dirty, errors } = useFormState();
    const setValue = (value) => setFieldValue(name, value);

    return {
        value: values[name], 
        valid: errors.findIndex(element => element.name === name) < 0, 
        dirty: dirty.findIndex(element => element === name) >= 0, 
        setValue
    };
};


